<template>
  <div id="new-zgb-team-members-list">
    <v-card class="list-card elevation-6">
      <v-sheet class="list-header" tile>
        <div class="header-content">
          <!-- Top Left: Title with Icon -->
          <div class="header-title-section">
            <div class="title-icon-wrapper">
              <v-icon large color="white" class="title-icon"
                >mdi-account-tie</v-icon
              >
            </div>
            <div class="title-content">
              <h2 class="page-title">{{ PageTitle }}</h2>
              <p class="page-desc">{{ PageDescription }}</p>
            </div>
          </div>

          <!-- Top Right: Actions -->
          <div class="header-actions-top">
            <v-chip
              small
              color="rgba(255, 255, 255, 0.25)"
              text-color="white"
              class="year-chip-top"
            >
              <v-icon left x-small>mdi-calendar-check</v-icon>
              {{ NextYearName }}
            </v-chip>

            <v-btn
              icon
              small
              color="white"
              class="action-btn"
              @click.prevent="refreshPageData"
              v-tooltip="'Refresh List'"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>

            <v-btn
              color="white"
              small
              class="action-btn-primary"
              @click.prevent="addRecordPrompt = true"
            >
              <v-icon left small>mdi-plus-circle</v-icon> Assign Member
            </v-btn>
          </div>
        </div>

        <!-- Search Bar Section -->
        <div class="header-search">
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            dense
            hide-details
            rounded
            clearable
            placeholder="Search by member name, designation, or LOM..."
            class="search-input-modern"
          ></v-text-field>
        </div>
      </v-sheet>

      <v-card-text>
        <div class="loading-wrap" v-if="LoadingFlag">
          <b-spinner
            variant="primary"
            type="grow"
            label="Loading..."
          ></b-spinner>
        </div>

        <v-container fluid>
          <v-row>
            <v-col cols="12">
              <v-data-iterator
                v-if="rows.length > 0"
                :items="rows"
                :items-per-page="-1"
                hide-default-footer
              >
                <template v-slot:default="props">
                  <v-row dense>
                    <v-col
                      v-for="item in props.items"
                      :key="item.MemberId"
                      cols="12"
                      sm="6"
                      md="4"
                      lg="3"
                    >
                      <v-card class="member-card" hover elevation="4">
                        <div class="card-header-badge">
                          <v-chip
                            x-small
                            :color="item.ActiveStatus == 1 ? 'green' : 'red'"
                            text-color="white"
                            class="position-chip"
                          >
                            <v-icon x-small left>
                              {{
                                item.ActiveStatus == 1
                                  ? "mdi-check"
                                  : "mdi-close"
                              }}
                            </v-icon>
                            {{ item.ActiveStatusTxt }}
                          </v-chip>
                        </div>

                        <v-card-text class="card-top">
                          <div class="avatar-wrap">
                            <v-avatar size="92" class="avatar-elev">
                              <v-img
                                :src="item.ProfilePic"
                                :alt="item.MemberFullname"
                              />
                            </v-avatar>
                          </div>

                          <div class="member-info">
                            <div class="member-name">
                              {{ item.MemberFullname }}
                            </div>
                            <div class="member-designation">
                              <v-icon x-small class="mr-1"
                                >mdi-badge-account</v-icon
                              >
                              {{ item.DesignationName }}
                            </div>
                            <div class="member-lom">
                              <v-icon x-small class="mr-1"
                                >mdi-map-marker</v-icon
                              >
                              {{ item.LomName }}
                            </div>
                          </div>
                        </v-card-text>

                        <v-divider></v-divider>

                        <v-card-actions class="card-actions">
                          <v-tooltip
                            bottom
                            v-if="item.DesignationId != '' && editFlag"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                small
                                color="primary"
                                v-bind="attrs"
                                v-on="on"
                                @click="editData(item)"
                                class="edit-btn"
                              >
                                <v-icon>mdi-pencil</v-icon>
                              </v-btn>
                            </template>
                            <span>Edit Member</span>
                          </v-tooltip>

                          <v-spacer></v-spacer>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                </template>
              </v-data-iterator>

              <div v-else class="no-records">
                <v-row align="center" justify="center">
                  <v-col cols="12" md="8" class="text-center">
                    <div class="empty-icon-wrapper">
                      <v-icon color="primary" size="64"
                        >mdi-account-group-outline</v-icon
                      >
                    </div>
                    <h3 class="mt-4 empty-title">No members assigned yet</h3>
                    <div class="caption grey--text empty-subtitle">
                      Click <strong>Assign Member</strong> to add ZGB members
                      for {{ NextYearName }}.
                    </div>
                    <div class="mt-5 empty-actions">
                      <v-btn
                        color="success"
                        elevation="4"
                        @click.prevent="addRecordPrompt = true"
                      >
                        <v-icon left>mdi-plus-circle</v-icon> Assign Member
                      </v-btn>
                      <v-btn
                        small
                        class="ml-3"
                        color="secondary"
                        elevation="2"
                        @click.prevent="refreshPageData"
                      >
                        <v-icon left small>mdi-refresh</v-icon>
                        Refresh
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-col>
          </v-row>

          <v-row class="notes-row">
            <v-col cols="12" md="8">
              <v-card class="info-card" elevation="2">
                <v-card-title class="info-title">
                  <v-icon left color="blue">mdi-information-variant</v-icon>
                  Important Guidelines
                </v-card-title>
                <v-card-text class="info-content">
                  <div class="guideline-item">
                    <v-icon small color="success" class="guideline-icon"
                      >mdi-check-circle</v-icon
                    >
                    <span
                      >As per Article 4.2 of policy manual part 2, all elective
                      post members should be active members.</span
                    >
                  </div>
                  <div class="guideline-item">
                    <v-icon small color="success" class="guideline-icon"
                      >mdi-check-circle</v-icon
                    >
                    <span
                      >All the elective officers should upload their age proof
                      document under their members profile update page.</span
                    >
                  </div>
                  <div class="guideline-item">
                    <v-icon small color="success" class="guideline-icon"
                      >mdi-check-circle</v-icon
                    >
                    <span
                      >To assign ZGB members for the next year click on the
                      <strong>Assign Member</strong> button.</span
                    >
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
    <v-dialog v-model="addRecordPrompt" persistent scrollable max-width="75%">
      <div style="max-height: 80vh; overflow-y: auto">
        <new-zgb-team-members-modify
          pageDescription="Assign Zone Governing board members for the year"
          :addRecordPrompt="addRecordPrompt"
          @hideAddRecordPrompt="hideAddRecordPrompt"
          v-if="addRecordPrompt"
        />
      </div>
    </v-dialog>

    <v-dialog v-model="editRecordPrompt" persistent scrollable max-width="75%">
      <div style="max-height: 80vh; overflow-y: auto">
        <new-zgb-team-members-edit
          pageDescription="Edit Zone Governing board members for the year"
          :editRecordPrompt="editRecordPrompt"
          :recordData="selectedData"
          @hideEditRecordPrompt="hideEditRecordPrompt"
          v-if="editRecordPrompt"
        />
      </div>
    </v-dialog>
  </div>
</template>
<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  ADD_BODY_CLASSNAME,
  REMOVE_BODY_CLASSNAME,
} from "@/core/services/store/htmlclass.module.js";
import NewZgbTeamMembersModify from "@/view/pages/erp/team-members/new-zgb/NewZgbTeamMembersModify.vue";
import NewZgbTeamMembersEdit from "@/view/pages/erp/team-members/new-zgb/NewZgbTeamMembersEdit.vue";

export default {
  mixins: [common],
  components: {
    NewZgbTeamMembersModify,
    NewZgbTeamMembersEdit,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,

      refreshButtonFlag: false,
      floatButtonFlag: false,

      rows: [],
      tableSelectFlag1: false,
      tableColumns1: [],
      selected: [],
      search: "",
      CurrentYearId: 0,

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      editFlag: true,
      selectedData: {},

      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,

      zone: {},
      lom: {},

      name: "",
      nameRules: [
        (v) => !!v || "Name is required",
        (v) => (v && v.length <= 10) || "Name must be less than 10 characters",
      ],
      email: "",
      emailRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],
      select: "Item 1",
      items: ["Item 1", "Item 2", "Item 3", "Item 4"],
      checkbox: false,

      FromZoneCodeRules: [(v) => !!v || "Zone is required"],
      FromZoneCode: "",
      FromZoneCodeOptions: [],
      FromZoneCodeOptionsLoading: false,

      FromLomCodeRules: [(v) => !!v || "LOM is required"],
      FromLomCode: "",
      FromLomCodeOptions: [],
      FromLomCodeOptionsLoading: false,

      ToZoneCodeRules: [(v) => !!v || "Zone is required"],
      ToZoneCode: "",
      ToZoneCodeOptions: [],
      ToZoneCodeOptionsLoading: false,

      ToLomCodeRules: [(v) => !!v || "LOM is required"],
      ToLomCode: "",
      ToLomCodeOptions: [],
      ToLomCodeOptionsLoading: false,

      SearchId: "",
      SearchIdRules: [(v) => !!v || "Search Type is required"],
      SearchIdOptions: [
        { value: "", text: "--Select Type--" },
        { value: 1, text: "By LOM" },
        { value: 2, text: "By Membership Id" },
      ],

      MembershipId: "",
      MembershipIdRules: [(v) => !!v || "Membership Id is required"],

      YearHalfId: "",
      YearHalfIdRules: [(v) => !!v || "Year Half is required"],
      YearHalfIdOptions: [
        { value: "", text: "--Select Half--" },
        { value: "First", text: "First Half" },
        { value: "Second", text: "Second Half" },
      ],

      YearIdRules: [(v) => !!v || "Year is required"],
      YearId: "",
      YearIdOptions: [],
      YearIdOptionsLoading: false,

      ApprovalDescription: "",
      ApprovalDescriptionRules: [],

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = NextJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        this.pageData();
      }
    },
    SearchId: function () {
      console.log("watch SearchId");
      this.rows = [];
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "zgb_dis",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.getTableRecords();
    },
    // code 1
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    hideAddRecordPrompt() {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      this.getTableRecords();
    },
    resetForm() {
      this.search = "";
      this.rows = [];
      this.selected = [];
      this.$refs.form3.reset();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    hideEditRecordPrompt() {
      console.log("hideEditRecordPrompt called");
      this.editRecordPrompt = false;
      this.getTableRecords();
    },
    editData(tr) {
      console.log("editData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.selectedData = tr;
        this.editRecordPrompt = true;
      } else {
        var message = "Kindly select one member to edit";
        this.toast("error", message);
      }
    },
    getTableRecords() {
      console.log("getTableRecords called");

      var ZoneCode = this.$session.get("ZoneId");
      ZoneCode = ZoneCode == (null || undefined) ? "" : ZoneCode;
      console.log("ZoneCode=" + ZoneCode);

      if (ZoneCode != null) {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/year-wise-designation/lists";
        var upload = {
          UserInterface: 4,
          OrganizationTypeId: 2,
          YearCode: this.CurrentYearId,
          ZoneCode: ZoneCode,
          ExceptDesignationId: [1, 5, 7],
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.rows = [];
        this.ResultFlag = false;
        this.LoadingFlag = true;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.ResultFlag = true;
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.rows = records.TableData;
              thisIns.tableColumns1 = records.TableHeader;
              thisIns.tableSelectFlag1 = records.TableSelectFlag;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.toast("error", error);
            thisIns.ResultFlag = true;
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "Your session is expired. Kindly login again. ";
        console.log("error=" + message);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
/* filepath: /home/hplap/Documents/web_app/jci_india_erp_app_new/src/view/pages/erp/team-members/new-zgb/NewZgbTeamMembersList.vue */
/* Professional modern UI with icons and proper header alignment */

#new-zgb-team-members-list {
  font-family: "Inter", "Roboto", sans-serif;

  .list-card {
    border-radius: 12px;
    overflow: visible;
    background: #ffffff;
  }

  /* Modern Professional Header */
  .list-header {
    background: linear-gradient(90deg, #1976d2 0%, #4e8ef7 100%);
    padding: 0;
    border-radius: 12px 12px 0 0;
  }

  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 20px 24px;
  }

  .header-title-section {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
  }

  .title-icon-wrapper {
    width: 56px;
    height: 56px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(4px);
  }

  .title-icon {
    font-size: 32px !important;
  }

  .title-content {
    flex: 1;
  }

  .page-title {
    color: #ffffff;
    font-size: 24px;
    font-weight: 700;
    margin: 0;
    line-height: 1.2;
  }

  .page-desc {
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
    font-weight: 500;
    margin: 4px 0 0 0;
  }

  .header-actions-top {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }

  .year-chip-top {
    background: rgba(255, 255, 255, 0.25) !important;
    color: #ffffff !important;
    white-space: nowrap;
  }

  .action-btn {
    transition: all 0.2s ease;
    background: rgba(255, 255, 255, 0.15) !important;

    &:hover {
      background: rgba(255, 255, 255, 0.25) !important;
      transform: translateY(-2px);
    }
  }

  .action-btn-primary {
    background: rgba(255, 255, 255, 0.25) !important;
    color: #ffffff !important;
    font-weight: 600;

    &:hover {
      background: rgba(255, 255, 255, 0.35) !important;
      transform: translateY(-2px);
    }
  }

  .header-search {
    padding: 0 24px 16px 24px;
  }

  .search-input-modern {
    ::v-deep .v-input__control,
    ::v-deep input {
      background: rgba(255, 255, 255, 0.95) !important;
      border-radius: 6px;
    }
  }

  .loading-wrap {
    display: flex;
    justify-content: center;
    margin: 18px 0;
  }

  /* Member card */
  .member-card {
    transition: transform 0.18s ease, box-shadow 0.18s ease;
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: auto;
    position: relative;
  }

  .member-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 32px rgba(11, 22, 60, 0.12);
  }

  .card-header-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 10;
  }

  .position-chip {
    font-weight: 600;
  }

  .card-top {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px;
  }

  .avatar-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .avatar-elev {
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
    border-radius: 12px;
    overflow: hidden;
  }

  .member-info {
    flex: 1;

    .member-name {
      font-weight: 700;
      font-size: 16px;
      color: #0f172a;
      line-height: 1.2;
    }

    .member-designation {
      font-size: 13px;
      color: #2563eb;
      margin-top: 6px;
      display: flex;
      align-items: center;
      font-weight: 500;
    }

    .member-lom {
      font-size: 12px;
      color: #ef4444;
      margin-top: 4px;
      display: flex;
      align-items: center;
      font-weight: 500;
    }
  }

  .card-actions {
    padding: 8px 12px;
    display: flex;
    align-items: center;
  }

  .edit-btn {
    transition: all 0.2s ease;

    &:hover {
      color: #1565c0 !important;
      transform: scale(1.1);
    }
  }

  /* Empty state */
  .no-records {
    padding: 60px 20px;
    background: rgba(59, 130, 246, 0.03);
  }

  .empty-icon-wrapper {
    display: flex;
    justify-content: center;
    margin: 20px 0;
  }

  .empty-title {
    font-size: 20px;
    font-weight: 700;
    color: #0f172a;
  }

  .empty-subtitle {
    font-size: 14px;
    margin-top: 8px !important;
  }

  .empty-actions {
    display: flex;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  /* Guidelines/Notes */
  .notes-row {
    margin-top: 24px;
  }

  .info-card {
    border-radius: 12px;
    background: #ffffff;
    overflow: hidden;
    border: 1px solid rgba(15, 23, 42, 0.06);
  }

  .info-title {
    background: linear-gradient(90deg, #1976d2 0%, #4e8ef7 100%);
    color: white;
    font-weight: 700;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .info-content {
    padding: 16px;
  }

  .guideline-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 14px;
    line-height: 1.6;
    color: #4b5563;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .guideline-icon {
    flex-shrink: 0;
    margin-top: 2px;
  }

  /* Responsive tweaks */
  @media (max-width: 960px) {
    .list-header {
      padding: 0;
    }

    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
      padding: 16px;
    }

    .header-title-section {
      width: 100%;
    }

    .header-actions-top {
      width: 100%;
      justify-content: flex-end;
    }

    .header-search {
      padding: 0 16px 12px 16px;
    }

    .search-input-modern {
      width: 100%;
    }

    .member-card {
      min-height: auto;
    }

    .empty-actions {
      flex-direction: column;
    }

    .empty-actions .v-btn {
      width: 100%;
    }
  }

  @media (max-width: 600px) {
    .page-title {
      font-size: 18px;
    }

    .page-desc {
      font-size: 12px;
    }

    .header-actions-top {
      gap: 8px;
    }

    .action-btn-primary {
      font-size: 12px;
      padding: 4px 8px;
    }
  }
}
</style>